import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Checkbox } from '../../../../components/inputs/Checkbox';
import { FileField, USER_DOCUMENT_MAX_BYTES } from '../../../../components/inputs/FileField';
import { LazyRichTextField } from '../../../../components/inputs/LazyRichTextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { focusValidationError } from '../../../../lib/validationFocus';
import { mySurveysApi } from '../mySurveysApi';
import { TRAINING_REPORT_MAX_ATTACHMENTS, type MySurveyTraining } from '../mySurveyTypes';
import { sampleTrainingReport } from '../utils/sampleTrainingReport';

const ID = 'my-survey-training-report';

/** The markup with its tags gone — what the editor's own budget counts, and what "empty" means. */
function plainText(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
}

/**
 * Submit report — or edit it — on a COMPLETED training (product call, 12 Oct 2026): a written
 * report, and optionally SEVERAL attached files — CERTIFICATES in a field of their own, apart from
 * the other attachments (product call, 13 Oct 2026; on a recommendation's training). Learning & Development reads all of it on the
 * plan. The report can be revised at any time; the date it was first submitted stays.
 *
 * **Writes, in order**: the report, then the removals, then each new file in turn — a file is
 * filed AGAINST a report, so the server refuses one before there is a report, and removing first
 * frees places under the cap. If a file fails part-way the form STAYS OPEN and says which: what
 * was already saved is saved, and the files not yet sent are still chosen.
 *
 * Each file is PDF, PNG or JPEG up to 10 MB, verified server-side by its own leading bytes; a
 * report carries at most ten. New files are ADDED to the ones attached — the ticks remove.
 *
 * **Autogenerate fills the REPORT** (product call, 13 Oct 2026) with a synthetic one, in a
 * REMOUNTED editor — a rich-text field takes its value once, so the `key` is bumped. It does NOT
 * touch the attachments: a file's type is verified by its own leading bytes, so none can be faked,
 * and the ones already attached or ticked for removal are the reader's decisions.
 */
export function MySurveyTrainingReportModal({
  surveyId,
  recommendationId,
  training,
  onClose,
  onSaved,
}: {
  surveyId: string;
  /**
   * Set when the training is taken on a SENT TRAINING RECOMMENDATION (product call, 13 Oct 2026):
   * the form then writes that employee's own track on it, not the plan's. Absent, it is the
   * development plan's own training, as before.
   */
  recommendationId?: string;
  training: MySurveyTraining;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  // `!= null`: it came off the wire, where an absent value is undefined.
  const editing = training.reportAt != null;
  const attached = training.reportAttachments ?? [];

  const [report, setReport] = useState(training.report ?? '');
  // Bumped by Autogenerate — the editor is uncontrolled after mount, so a new value needs a new editor.
  const [editorKey, setEditorKey] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  // Certificates are asked apart from the other attachments — on a recommendation's training only.
  const [certificates, setCertificates] = useState<File[]>([]);
  const onRecommendation = recommendationId != null;
  // The ids of attached files ticked for removal.
  const [removing, setRemoving] = useState<string[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  // "Uploading 2 of 3 — 40%", while the files go up; null otherwise.
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const reportEmpty = plainText(report) === '';
  // Every file to send, certificates first — each remembers which field it came from.
  const uploads = [...certificates.map((file) => ({ file, certificate: true })), ...files.map((file) => ({ file, certificate: false }))];
  const tooLarge = uploads.some(({ file }) => file.size > USER_DOCUMENT_MAX_BYTES);
  // What the report would carry once this is submitted — the server's cap, said before it refuses.
  const resulting = attached.length - removing.length + uploads.length;
  const tooMany = resulting > TRAINING_REPORT_MAX_ATTACHMENTS;
  const canSave = !reportEmpty && !tooLarge && !tooMany && confirmed && !saving;

  function toggleRemoving(id: string, checked: boolean) {
    setRemoving((all) => (checked ? [...all, id] : all.filter((existing) => existing !== id)));
  }

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);
    setProgress(null);

    try {
      // ONE report, written on the plan's training or on a recommendation's — the same three calls either way.

      if (onRecommendation) await mySurveysApi.saveRecommendationReport(surveyId, recommendationId, { report });
      else await mySurveysApi.saveTrainingReport(surveyId, { report });

      // Removals FIRST — they free places under the cap for the files that follow.
      for (const id of removing) {
        if (onRecommendation) await mySurveysApi.removeRecommendationReportAttachment(surveyId, recommendationId, id);
        else await mySurveysApi.removeTrainingReportAttachment(surveyId, id);
      }

      for (const [index, { file, certificate }] of uploads.entries()) {
        const step = uploads.length > 1 ? `Uploading ${index + 1} of ${uploads.length}` : 'Uploading';

        setProgress(step);
        const report = (percent: number) => setProgress(`${step} — ${percent}%`);

        if (onRecommendation) await mySurveysApi.attachRecommendationReport(surveyId, recommendationId, file, report, certificate);
        else await mySurveysApi.attachTrainingReport(surveyId, file, report);
      }

      toast.success(editing ? 'Saved your report.' : 'Submitted your report.');
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Your report could not be saved.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit report' : 'Submit report'}
      // One step wider than the other forms here (product call, 13 Oct 2026): the editor's toolbar wraps at `xl`.
      size="2xl"
      id={`${ID}-modal`}
      autogenerate={{
        onGenerate: () => {
          setReport(sampleTrainingReport(training.title));
          setEditorKey((k) => k + 1);
        },
        disabled: saving,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id={`${ID}-submit`}
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel={progress ?? 'Submitting'}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <LazyRichTextField
          key={editorKey}
          id={`${ID}-report`}
          label="Report"
          required
          maxLength={8000}
          description="What the training covered and what you took from it. Learning & Development reads this on your development plan."
          value={report}
          onChange={setReport}
          error={reportEmpty ? 'Write the report.' : error?.fieldError('report')}
          disabled={saving}
        />

        {/* CERTIFICATES, in a field of their own — the same files, told apart from the other attachments. */}
        {onRecommendation ? (
          <FileField
            id={`${ID}-certificates`}
            label="Certificates"
            description={`Optional — the certificates you received for the training. Choose one or several: PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)} each. They count toward the ${TRAINING_REPORT_MAX_ATTACHMENTS} files a report may carry.`}
            files={certificates}
            onChange={setCertificates}
            multiple
            disabled={saving}
          />
        ) : null}

        {certificates.length > 1 ? (
          <ul className="flex flex-col gap-0.5 text-xs text-slate-600">
            {certificates.map((file) => (
              <li key={`${file.name}-${file.size}`} className="break-all">
                {file.name} · {formatBytes(file.size)}
              </li>
            ))}
          </ul>
        ) : null}

        <FileField
          id={`${ID}-file`}
          // "Other" beside the Certificates field; on the plan's own training it is the only file field.
          label={onRecommendation ? 'Other Attachments' : 'Attachments'}
          description={`Optional — other documents for the training. Choose one or several: PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)} each, at most ${TRAINING_REPORT_MAX_ATTACHMENTS} on a report. Each type is verified against the file's own leading bytes rather than its name.`}
          files={files}
          onChange={setFiles}
          multiple
          disabled={saving}
          error={
            tooMany
              ? `A report carries at most ${TRAINING_REPORT_MAX_ATTACHMENTS} files — this would make ${resulting}. Choose fewer, or remove some below.`
              : (error?.fieldError('file') ?? error?.fieldError('fileName'))
          }
        />

        {/* What was CHOSEN, by name — the box itself only says how many. */}
        {files.length > 1 ? (
          <ul className="flex flex-col gap-0.5 text-xs text-slate-600">
            {files.map((file) => (
              <li key={`${file.name}-${file.size}`} className="break-all">
                {file.name} · {formatBytes(file.size)}
              </li>
            ))}
          </ul>
        ) : null}

        {/* What is attached NOW. New files are ADDED to these; a tick removes one — said before the confirm tick. */}
        {attached.length > 0 ? (
          <fieldset className="flex flex-col gap-1.5">
            <legend className="pb-1.5 text-sm text-slate-700">Attached now — tick a file to remove it</legend>
            {attached.map((attachment) => (
              <label key={attachment.id} htmlFor={`${ID}-remove-${attachment.id}`} className="flex items-center gap-2.5">
                <Checkbox
                  id={`${ID}-remove-${attachment.id}`}
                  checked={removing.includes(attachment.id)}
                  onChange={(e) => toggleRemoving(attachment.id, e.target.checked)}
                  disabled={saving}
                />
                <span
                  className={`min-w-0 break-all text-sm ${removing.includes(attachment.id) ? 'text-slate-400 line-through' : 'text-slate-600'}`}
                >
                  {attachment.fileName}
                  {attachment.isCertificate === true ? <span className="text-slate-400"> · Certificate</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}
      </div>
    </Modal>
  );
}
